import { useCallback, useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import cogoToast from 'cogo-toast'
import { AUTO_BOTS } from './autoBotsConfig'

const isEnabled = (value) => value?.is_active !== false

const toBot = (cfg, stored = {}) => ({
  ...cfg,
  text: stored.text || '',
  is_active: isEnabled(stored),
  available_variables:
    Array.isArray(stored.available_variables) &&
    stored.available_variables.length
      ? stored.available_variables
      : cfg.defaultVariables || [],
})

export const useAutoBotsSettings = () => {
  const [bots, setBots] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await superAxiosRequest({
        link: 'settings/project_settings',
        method: 'get',
        crm: true,
      })
      const byKey = Object.fromEntries(
        (response.data || []).map((s) => [s.key, s.value || {}]),
      )
      setBots(AUTO_BOTS.map((cfg) => toBot(cfg, byKey[cfg.key])))
    } catch (e) {
      console.error('Не удалось загрузить настройки автоботов', e)
      cogoToast.error('Не удалось загрузить настройки автоботов')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // ВАЖНО: payload передаётся ВТОРЫМ аргументом, а не внутри config
  const saveBot = useCallback(async (bot) => {
    const payload = {
      value: {
        text: bot.text,
        available_variables: bot.available_variables,
        is_active: bot.is_active,
      },
    }

    const response = await superAxiosRequest(
      {
        link: `settings/project_settings/${bot.key}`,
        method: 'put',
        crm: true,
      },
      payload,
    )

    // Мержим ответ сервера, чтобы UI отражал реальное состояние
    const updatedValue = response?.data?.value || payload.value
    const next = {
      ...bot,
      text: updatedValue.text ?? bot.text,
      is_active: updatedValue.is_active ?? bot.is_active,
      available_variables:
        updatedValue.available_variables ?? bot.available_variables,
    }

    setBots((prev) => prev.map((b) => (b.key === bot.key ? next : b)))
    return next
  }, [])

  const toggleBot = useCallback(
    async (bot, nextActive) => {
      const updated = { ...bot, is_active: nextActive }
      setBots((prev) => prev.map((b) => (b.key === bot.key ? updated : b)))
      try {
        await saveBot(updated)
      } catch (e) {
        console.error('[toggleBot] fail', {
          message: e.message,
          response: e.response?.data,
          config: e.config,
        })
        setBots((prev) =>
          prev.map((b) =>
            b.key === bot.key ? { ...b, is_active: !nextActive } : b,
          ),
        )
        cogoToast.error(
          e?.response?.data?.detail || 'Не удалось изменить статус автобота',
        )
      }
    },
    [saveBot],
  )

  return { bots, isLoading, saveBot, toggleBot, refetch: fetchAll }
}
